import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Package, Plus, Pencil, Trash2, Check, X, Clock, CheckCircle, Truck, AlertCircle, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { format, addWeeks } from "date-fns";
import OrderActions from "@/components/OrderActions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const garmentTypes = ["Coat", "2pcs Suit", "3pcs Suit", "Shirt", "Pants", "Vest", "Tuxedo", "Overcoat"];
const statusOptions = [
  { value: "pending", label: "Pending", color: "text-amber-400", bg: "bg-amber-500/10", icon: Clock },
  { value: "in_production", label: "In Production", color: "text-blue-400", bg: "bg-blue-500/10", icon: AlertCircle },
  { value: "ready", label: "Ready for Pickup", color: "text-green-400", bg: "bg-green-500/10", icon: CheckCircle },
  { value: "delivered", label: "Delivered", color: "text-purple-400", bg: "bg-purple-500/10", icon: Truck }
];

const AdminOrderStatusPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Form state
  const [formData, setFormData] = useState({
    order_number: "",
    client_name: "",
    garment_type: "",
    status: "pending",
    order_date: "",
    arrival_date: "",
    notes: ""
  });

  // Auto-calculate arrival date when order date changes
  const handleOrderDateChange = (date) => {
    if (date) {
      // Parse the date string as local date to avoid timezone issues
      const [year, month, day] = date.split('-').map(Number);
      const orderDate = new Date(year, month - 1, day);
      const arrivalDate = addWeeks(orderDate, 4);
      
      setFormData(prev => ({
        ...prev,
        order_date: date,
        arrival_date: format(arrivalDate, "yyyy-MM-dd")
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        order_date: "",
        arrival_date: ""
      }));
    }
  };

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
    } else {
      setIsAuthenticated(true);
      fetchOrders();
    }
  }, [navigate]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.order_number || !formData.client_name || !formData.garment_type) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (editingOrder) {
        await axios.put(`${API}/orders/${editingOrder.id}`, formData);
        toast.success("Order updated successfully");
      } else {
        await axios.post(`${API}/orders`, formData);
        toast.success("Order created successfully");
      }
      
      setShowAddForm(false);
      setEditingOrder(null);
      setFormData({
        order_number: "",
        client_name: "",
        garment_type: "",
        status: "pending",
        order_date: "",
        arrival_date: "",
        notes: ""
      });
      fetchOrders();
    } catch (error) {
      console.error("Failed to save order:", error);
      toast.error("Failed to save order");
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      order_number: order.order_number,
      client_name: order.client_name,
      garment_type: order.garment_type,
      status: order.status,
      order_date: order.order_date || "",
      arrival_date: order.arrival_date || "",
      notes: order.notes || ""
    });
    setShowAddForm(true);
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;
    
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success("Order deleted");
      fetchOrders();
    } catch (error) {
      console.error("Failed to delete order:", error);
      toast.error("Failed to delete order");
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { status: newStatus });
      toast.success("Status updated");
      fetchOrders();
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status");
    }
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingOrder(null);
    setFormData({
      order_number: "",
      client_name: "",
      garment_type: "",
      status: "pending",
      order_date: "",
      arrival_date: "",
      notes: ""
    });
  };

  const getStatusConfig = (status) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  const filteredOrders = filterStatus === "all" 
    ? orders 
    : orders.filter(o => o.status === filterStatus);

  // Group orders by status for summary
  const statusCounts = {
    pending: orders.filter(o => o.status === "pending").length,
    in_production: orders.filter(o => o.status === "in_production").length,
    ready: orders.filter(o => o.status === "ready").length,
    delivered: orders.filter(o => o.status === "delivered").length
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button
              onClick={() => navigate("/admin")}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Dashboard
            </button>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <Package size={24} className="text-amber-400" />
                </div>
                <div>
                  <h1 className="font-serif text-2xl sm:text-3xl tracking-tight">
                    Order Status
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Track and manage all client orders
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setShowAddForm(true)}
                className="bg-primary text-primary-foreground hover:bg-foreground"
              >
                <Plus size={18} className="mr-2" />
                Add Order
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Status Summary Cards */}
      <section className="py-6">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {statusOptions.map((status) => (
              <motion.div
                key={status.value}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`${status.bg} border border-white/10 p-4 cursor-pointer hover:border-white/20 transition-colors ${filterStatus === status.value ? 'ring-2 ring-primary' : ''}`}
                onClick={() => setFilterStatus(filterStatus === status.value ? "all" : status.value)}
              >
                <div className="flex items-center gap-3">
                  <status.icon size={24} className={status.color} />
                  <div>
                    <p className="text-2xl font-bold">{statusCounts[status.value]}</p>
                    <p className="text-xs text-muted-foreground">{status.label}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          {filterStatus !== "all" && (
            <div className="mt-4">
              <button
                onClick={() => setFilterStatus("all")}
                className="text-sm text-primary hover:underline"
              >
                Clear filter - Show all orders
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Add/Edit Form */}
      {showAddForm && (
        <section className="py-4">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-primary/30 p-6"
            >
              <h3 className="font-serif text-lg mb-4">
                {editingOrder ? "Edit Order" : "Add New Order"}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Order Number *</label>
                    <Input
                      placeholder="e.g., DDLR25100015"
                      value={formData.order_number}
                      onChange={(e) => setFormData({ ...formData, order_number: e.target.value })}
                      className="bg-background border-white/10"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Client Name *</label>
                    <Input
                      placeholder="Client name"
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      className="bg-background border-white/10"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Garment Type *</label>
                    <Select 
                      value={formData.garment_type} 
                      onValueChange={(value) => setFormData({ ...formData, garment_type: value })}
                    >
                      <SelectTrigger className="bg-background border-white/10">
                        <SelectValue placeholder="Select garment" />
                      </SelectTrigger>
                      <SelectContent>
                        {garmentTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Status</label>
                    <Select 
                      value={formData.status} 
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger className="bg-background border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-primary mb-1 flex items-center gap-1">
                      <Calendar size={12} /> Order Date
                    </label>
                    <Input
                      type="date"
                      value={formData.order_date}
                      onChange={(e) => handleOrderDateChange(e.target.value)}
                      className="bg-background border-primary/30"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-primary mb-1 flex items-center gap-1">
                      <Package size={12} /> Arrival Date (4 weeks)
                    </label>
                    <Input
                      type="date"
                      value={formData.arrival_date}
                      onChange={(e) => setFormData({ ...formData, arrival_date: e.target.value })}
                      className="bg-background border-primary/30"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Notes (optional)</label>
                    <Input
                      placeholder="Additional notes..."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="bg-background border-white/10"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button type="submit" className="bg-primary text-primary-foreground hover:bg-foreground">
                    <Check size={16} className="mr-2" />
                    {editingOrder ? "Update Order" : "Create Order"}
                  </Button>
                  <Button type="button" variant="outline" onClick={cancelForm} className="border-white/20">
                    <X size={16} className="mr-2" />
                    Cancel
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </section>
      )}

      {/* Orders Table */}
      <section className="py-6">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-white/10 overflow-hidden"
          >
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Loading orders...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="p-12 text-center">
                <Package size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">
                  {filterStatus !== "all" ? "No orders with this status" : "No orders yet"}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Click "Add Order" to create your first order
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-primary/5 border-b border-white/10">
                      <th className="text-left p-4 text-sm font-medium text-primary">Order</th>
                      <th className="text-left p-4 text-sm font-medium text-primary">Client Name</th>
                      <th className="text-left p-4 text-sm font-medium text-primary">Garment</th>
                      <th className="text-left p-4 text-sm font-medium text-primary">Order Date</th>
                      <th className="text-left p-4 text-sm font-medium text-primary">Arrival Date</th>
                      <th className="text-left p-4 text-sm font-medium text-primary">Status</th>
                      <th className="text-left p-4 text-sm font-medium text-primary">Notes</th>
                      <th className="text-right p-4 text-sm font-medium text-primary">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order, idx) => {
                      const statusConfig = getStatusConfig(order.status);
                      return (
                        <motion.tr
                          key={order.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className="border-b border-white/5 hover:bg-white/5 transition-colors"
                        >
                          <td className="p-4">
                            <span className="font-mono text-sm">{order.order_number}</span>
                          </td>
                          <td className="p-4">
                            <span className="font-medium">{order.client_name}</span>
                          </td>
                          <td className="p-4">
                            <span className="text-muted-foreground">{order.garment_type}</span>
                          </td>
                          <td className="p-4">
                            {order.order_date ? (
                              <span className="text-sm">
                                {format(new Date(order.order_date + 'T00:00:00'), "MMM dd, yyyy")}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </td>
                          <td className="p-4">
                            {order.arrival_date ? (
                              <span className="text-sm text-primary flex items-center gap-1">
                                <Package size={12} />
                                {format(new Date(order.arrival_date + 'T00:00:00'), "MMM dd, yyyy")}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </td>
                          <td className="p-4">
                            <Select 
                              value={order.status} 
                              onValueChange={(value) => handleStatusChange(order.id, value)}
                            >
                              <SelectTrigger className={`w-[160px] ${statusConfig.bg} border-0`}>
                                <div className="flex items-center gap-2">
                                  <statusConfig.icon size={14} className={statusConfig.color} />
                                  <span className={statusConfig.color}>{statusConfig.label}</span>
                                </div>
                              </SelectTrigger>
                              <SelectContent>
                                {statusOptions.map((status) => (
                                  <SelectItem key={status.value} value={status.value}>
                                    <div className="flex items-center gap-2">
                                      <status.icon size={14} className={status.color} />
                                      <span>{status.label}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-4">
                            <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
                              {order.notes || "-"}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-end gap-2">
                              <OrderActions
                                orderId={order.id}
                                orderNumber={order.order_number}
                                defaultPhone={order.client_phone || ""}
                                variant="row"
                                testIdPrefix={`order-row-${order.id}`}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(order)}
                                className="hover:bg-white/10"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(order.id)}
                                className="hover:bg-red-500/10 text-red-400"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
          
          {/* Total count */}
          {!loading && filteredOrders.length > 0 && (
            <p className="text-sm text-muted-foreground mt-4">
              Showing {filteredOrders.length} of {orders.length} order(s)
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminOrderStatusPage;
